import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Bot,
  ChevronDown,
  Clock,
  Hand,
  HelpCircle,
  Link2,
  Network,
  Plus,
  Sparkles,
} from "lucide-react";

type OpsCoverage =
  | "unassessed"
  | "uncovered"
  | "covered"
  | "not_applicable";

type OpsOperator = "me" | "ai_agent" | "script" | "tool";

type OpsEvidenceStatus = "none" | "recent" | "stale" | "failed";

export type OpsJobView = {
  jobKey: string;
  domain: string;
  label: string;
  description: string | null;
  coverage: OpsCoverage;
  operator: OpsOperator | null;
  humanApproval: boolean;
  connectionProvider: string | null;
  agentName: string | null;
  isCustom: boolean;
  evidence: {
    status: OpsEvidenceStatus;
    source: string | null;
    evidenceLabel: "connector" | "agent-reported" | null;
    asOf: string | null;
    summary: string | null;
    payload: Record<string, unknown> | null;
    supporting?: Record<string, unknown> | null;
    connectionState?: string;
  };
  suggestedProvider: "instantly" | "buffer" | null;
};

type OpsDomainBlock = {
  id: string;
  label: string;
  jobs: OpsJobView[];
};

type OpsJobsResponse = {
  productId: string;
  productName: string;
  domains: OpsDomainBlock[];
  summary: {
    covered: number;
    uncovered: number;
    unassessed: number;
    recentEvidence: number;
  };
  message: string;
  capabilities?: { instantly?: string; buffer?: string };
};

type FilterId = "all" | "uncovered" | "depends_on_me" | "needs_attention";

type Props = {
  token: string;
  productId: string | null;
  productName: string | null;
  onPickProduct: (productId: string) => void;
  products: Array<{ id: string; name: string }>;
  instantlyConnected: boolean;
  bufferConnected: boolean;
  onManageConnection: () => void;
  onCreateQuest: (payload: {
    jobKey: string;
    label: string;
    gap: string;
  }) => void;
};

async function apiGet<T>(
  path: string,
  token: string,
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string }> {
  const res = await fetch(path, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    return { ok: false, status: res.status, error: (await res.text()).slice(0, 300) };
  }
  return { ok: true, data: (await res.json()) as T };
}

async function apiPatch<T>(
  path: string,
  token: string,
  body: unknown,
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string }> {
  const res = await fetch(path, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    return { ok: false, status: res.status, error: (await res.text()).slice(0, 400) };
  }
  return { ok: true, data: (await res.json()) as T };
}

async function apiPost<T>(
  path: string,
  token: string,
  body: unknown,
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string }> {
  const res = await fetch(path, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    return { ok: false, status: res.status, error: (await res.text()).slice(0, 400) };
  }
  return { ok: true, data: (await res.json()) as T };
}

function jobMatchesFilter(job: OpsJobView, filter: FilterId): boolean {
  if (filter === "all") return true;
  if (filter === "uncovered") return job.coverage === "uncovered";
  if (filter === "depends_on_me") {
    return (
      job.coverage === "covered" &&
      (job.operator === "me" || job.humanApproval)
    );
  }
  if (filter === "needs_attention") {
    return (
      job.coverage === "uncovered" ||
      job.evidence.status === "stale" ||
      job.evidence.status === "failed"
    );
  }
  return true;
}

function isLiveEvidence(job: OpsJobView): boolean {
  if (job.evidence.status !== "recent") return false;
  if (job.evidence.source === "instantly_warmup") return false;
  const kind = (job.evidence.payload as { kind?: string } | null)?.kind;
  return kind !== "warmup_support";
}

function isAutoOperator(job: OpsJobView): boolean {
  return (
    job.coverage === "covered" &&
    (job.operator === "ai_agent" ||
      job.operator === "script" ||
      job.operator === "tool")
  );
}

/** Honest ladder: how close this job is to hands-off automation. */
function automationScore(job: OpsJobView): number | null {
  if (job.coverage === "not_applicable") return null;
  if (job.coverage === "unassessed" || job.coverage === "uncovered") return 0;
  if (!job.operator || job.operator === "me") return 25;
  if (!isAutoOperator(job)) return 25;
  if (isLiveEvidence(job)) {
    return job.humanApproval ? 90 : 100;
  }
  if (job.evidence.status === "failed") return 55;
  if (job.evidence.status === "stale") return 75;
  return 50;
}

function automationLabel(score: number | null): string {
  if (score === null) return "Not applicable";
  if (score >= 100) return "Fully automated";
  if (score >= 90) return "Live · still needs approval";
  if (score >= 75) return "Wired · evidence stale";
  if (score >= 55) return "Wired · evidence failed";
  if (score >= 50) return "Declared automation";
  if (score >= 25) return "Manual · you run it";
  return "Not automated";
}

function domainAutomation(jobs: OpsJobView[]): {
  pct: number;
  full: number;
  scored: number;
} {
  const scored = jobs
    .map(automationScore)
    .filter((s): s is number => s !== null);
  if (!scored.length) return { pct: 0, full: 0, scored: 0 };
  const sum = scored.reduce((a, b) => a + b, 0);
  const full = scored.filter((s) => s >= 100).length;
  return {
    pct: Math.round(sum / scored.length),
    full,
    scored: scored.length,
  };
}

function nodeClass(job: OpsJobView, flashKey: string | null): string {
  const score = automationScore(job);
  const parts = ["op-ops-node"];
  parts.push(`op-ops-cov-${job.coverage}`);
  if (isLiveEvidence(job)) parts.push("is-live", "op-ops-evidence-recent");
  if (job.evidence.status === "stale") parts.push("op-ops-evidence-stale");
  if (job.evidence.status === "failed") parts.push("op-ops-evidence-failed");
  if (job.operator === "me") parts.push("op-ops-op-me");
  if (isAutoOperator(job)) parts.push("is-auto", "op-ops-op-agent");
  if (job.coverage === "uncovered") parts.push("is-gap");
  if (score !== null && score >= 100) parts.push("is-full-auto");
  if (flashKey === job.jobKey) parts.push("is-flash");
  return parts.join(" ");
}

function AutomationBar({
  pct,
  size = "md",
  lit = false,
}: {
  pct: number;
  size?: "sm" | "md" | "lg";
  lit?: boolean;
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div
      className={`op-ops-progress op-ops-progress-${size}${lit ? " is-lit" : ""}${
        clamped >= 100 ? " is-full" : ""
      }`}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Automation ${clamped}%`}
    >
      <span className="op-ops-progress-fill" style={{ width: `${clamped}%` }} />
    </div>
  );
}

function OperatorIcon({ job }: { job: OpsJobView }) {
  if (job.coverage === "unassessed") return <HelpCircle size={14} aria-hidden />;
  if (job.coverage === "uncovered") return <AlertTriangle size={14} aria-hidden />;
  if (job.operator === "ai_agent" || job.operator === "script") {
    return <Bot size={14} aria-hidden />;
  }
  if (job.operator === "tool") return <Link2 size={14} aria-hidden />;
  if (job.operator === "me") return <Hand size={14} aria-hidden />;
  return <Network size={14} aria-hidden />;
}

export function OpsPane(props: Props) {
  const [data, setData] = useState<OpsJobsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<FilterId>("all");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState("");
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const [expandedDomains, setExpandedDomains] = useState<Record<string, boolean>>(
    {},
  );
  const [agentNameDraft, setAgentNameDraft] = useState("");
  const [agentApproval, setAgentApproval] = useState(true);
  const [addDomain, setAddDomain] = useState<string | null>(null);
  const [addLabel, setAddLabel] = useState("");
  const [renameDraft, setRenameDraft] = useState("");

  const load = useCallback(async () => {
    if (!props.token || !props.productId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError("");
    const res = await apiGet<OpsJobsResponse>(
      `/v1/ops/jobs?productId=${encodeURIComponent(props.productId)}`,
      props.token,
    );
    setLoading(false);
    if (!res.ok) {
      setError(`Operations: ${res.status}`);
      setData(null);
      return;
    }
    setData(res.data);
  }, [props.token, props.productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected =
    data?.domains.flatMap((d) => d.jobs).find((j) => j.jobKey === selectedKey) ??
    null;

  useEffect(() => {
    if (selected) {
      setRenameDraft(selected.label);
      setAgentNameDraft(selected.agentName ?? "");
      setAgentApproval(selected.humanApproval);
      setExpandedDomains((prev) =>
        prev[selected.domain] ? prev : { ...prev, [selected.domain]: true },
      );
    }
  }, [selected?.jobKey, selected?.domain]);

  useEffect(() => {
    if (filter === "all" || !data) return;
    setExpandedDomains((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const domain of data.domains) {
        if (domain.jobs.some((j) => jobMatchesFilter(j, filter))) {
          if (!next[domain.id]) {
            next[domain.id] = true;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [filter, data]);

  function toggleDomain(domainId: string) {
    setExpandedDomains((prev) => ({
      ...prev,
      [domainId]: !prev[domainId],
    }));
  }

  function triggerFlash(jobKey: string) {
    setFlashKey(jobKey);
    window.setTimeout(() => {
      setFlashKey((cur) => (cur === jobKey ? null : cur));
    }, 720);
  }

  async function patchJob(
    jobKey: string,
    body: Record<string, unknown>,
    opts?: { flash?: boolean },
  ) {
    if (!props.productId) return;
    setBusy(jobKey);
    const res = await apiPatch<{ ok: boolean; job?: OpsJobView }>(
      `/v1/ops/jobs/${encodeURIComponent(jobKey)}`,
      props.token,
      { productId: props.productId, ...body },
    );
    setBusy("");
    if (!res.ok) {
      setError(res.error || "Update failed");
      return;
    }
    if (opts?.flash) triggerFlash(jobKey);
    await load();
  }

  async function addJob() {
    if (!props.productId || !addDomain || !addLabel.trim()) return;
    setBusy("add");
    const res = await apiPost<{ ok: boolean }>(`/v1/ops/jobs`, props.token, {
      productId: props.productId,
      domain: addDomain,
      label: addLabel.trim(),
    });
    setBusy("");
    if (!res.ok) {
      setError(res.error || "Add failed");
      return;
    }
    setAddLabel("");
    setAddDomain(null);
    await load();
  }

  const overallAuto = useMemo(() => {
    const allJobs = data?.domains.flatMap((d) => d.jobs) ?? [];
    return domainAutomation(allJobs);
  }, [data]);

  if (!props.productId) {
    return (
      <div className="op-ops-pick op-ops-stage">
        <div className="op-ops-embers" aria-hidden />
        <h2>Operations</h2>
        <p className="op-ops-sub">Pick a product to open its job inventory.</p>
        <div className="op-ops-pick-list">
          {props.products.map((p) => (
            <button
              key={p.id}
              type="button"
              className="op-primary"
              onClick={() => props.onPickProduct(p.id)}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const panelLive = selected ? isLiveEvidence(selected) : false;
  const selectedScore = selected ? automationScore(selected) : null;

  return (
    <div className="op-ops">
      <div className="op-ops-stage">
        <div className="op-ops-embers" aria-hidden />
        <div className="op-ops-heading">
          <div>
            <h2>Operations</h2>
            <small>{props.productName ?? "Product"}</small>
          </div>
        </div>

        {data ? (
          <div className="op-ops-hero-auto" aria-live="polite">
            <div className="op-ops-hero-auto-top">
              <div>
                <strong className="op-ops-hero-pct">{overallAuto.pct}%</strong>
                <span className="op-ops-hero-pct-label">Automated</span>
              </div>
              <p className="op-ops-hero-auto-copy">
                {overallAuto.full} of {overallAuto.scored} jobs fully automated
                {summary?.recentEvidence
                  ? ` · ${summary.recentEvidence} with live evidence`
                  : ""}
              </p>
            </div>
            <AutomationBar
              pct={overallAuto.pct}
              size="lg"
              lit={overallAuto.pct >= 100 || (summary?.recentEvidence ?? 0) > 0}
            />
          </div>
        ) : null}

        {summary ? (
          <div className="op-ops-hero-metrics" aria-live="polite">
            <div className="op-ops-metric">
              <strong>{overallAuto.full}</strong>
              <span>Fully auto</span>
            </div>
            <div className="op-ops-metric op-ops-metric-gap">
              <strong>{summary.uncovered}</strong>
              <span>Uncovered</span>
            </div>
            <div className="op-ops-metric op-ops-metric-quiet">
              <strong>{summary.unassessed}</strong>
              <span>Unassessed</span>
            </div>
            <div
              className={`op-ops-metric op-ops-metric-live${
                summary.recentEvidence > 0 ? " is-lit" : ""
              }`}
            >
              <strong>{summary.recentEvidence}</strong>
              <span>Recent evidence</span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="op-ops-filters" role="group" aria-label="Filter jobs">
        {(
          [
            ["all", "All"],
            ["uncovered", "Uncovered"],
            ["depends_on_me", "Depends on me"],
            ["needs_attention", "Needs attention"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-pressed={filter === id}
            onClick={() => setFilter(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? <p className="op-notice">Loading operations…</p> : null}
      {error ? <p className="op-notice err">{error}</p> : null}

      <div className={`op-ops-layout${selected ? " has-panel" : ""}`}>
        <div className="op-ops-domains">
          {(data?.domains ?? []).map((domain, domainIdx) => {
            const jobs = domain.jobs.filter((j) => jobMatchesFilter(j, filter));
            if (!jobs.length && filter !== "all") return null;
            const auto = domainAutomation(
              filter === "all" ? domain.jobs : jobs,
            );
            const open = Boolean(expandedDomains[domain.id]);
            return (
              <section
                key={domain.id}
                className={`op-ops-domain op-ops-domain-rail${
                  open ? " is-open" : " is-collapsed"
                }${auto.pct >= 100 ? " is-full-auto" : ""}`}
                style={{ ["--op-ops-i" as string]: domainIdx }}
              >
                <div className="op-ops-spine" aria-hidden />
                <header className="op-ops-domain-head">
                  <button
                    type="button"
                    className="op-ops-domain-toggle"
                    aria-expanded={open}
                    onClick={() => toggleDomain(domain.id)}
                  >
                    <ChevronDown
                      size={16}
                      className="op-ops-domain-chevron"
                      aria-hidden
                    />
                    <span className="op-ops-domain-title">
                      <h3>{domain.label}</h3>
                      <small>
                        {auto.full}/{auto.scored} fully auto · {auto.pct}%
                      </small>
                    </span>
                  </button>
                  <div className="op-ops-domain-meta">
                    <AutomationBar
                      pct={auto.pct}
                      size="sm"
                      lit={auto.pct >= 100}
                    />
                    <button
                      type="button"
                      className="op-linkish"
                      onClick={() => {
                        setExpandedDomains((prev) => ({
                          ...prev,
                          [domain.id]: true,
                        }));
                        setAddDomain(domain.id);
                        setAddLabel("");
                      }}
                    >
                      <Plus size={14} aria-hidden /> Add
                    </button>
                  </div>
                </header>
                {open ? (
                  <>
                    {addDomain === domain.id ? (
                      <div className="op-ops-add">
                        <input
                          className="op-field"
                          value={addLabel}
                          onChange={(e) => setAddLabel(e.target.value)}
                          placeholder="New recurring job"
                          autoFocus
                        />
                        <button
                          type="button"
                          className="op-primary"
                          disabled={!addLabel.trim() || busy === "add"}
                          onClick={() => void addJob()}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="op-linkish"
                          onClick={() => setAddDomain(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : null}
                    <ul className="op-ops-job-list">
                      {jobs.map((job, jobIdx) => {
                        const score = automationScore(job);
                        return (
                          <li
                            key={job.jobKey}
                            style={{
                              ["--op-ops-i" as string]:
                                domainIdx * 5 + jobIdx,
                            }}
                          >
                            <button
                              type="button"
                              className={`${nodeClass(job, flashKey)}${
                                selectedKey === job.jobKey ? " is-selected" : ""
                              }`}
                              onClick={() =>
                                setSelectedKey(
                                  selectedKey === job.jobKey
                                    ? null
                                    : job.jobKey,
                                )
                              }
                            >
                              {score !== null && score >= 100 ? (
                                <span
                                  className="op-ops-fire-ring"
                                  aria-hidden
                                />
                              ) : null}
                              <span className="op-ops-node-icon-wrap">
                                <span className="op-ops-orbit" aria-hidden />
                                <span className="op-ops-node-icon">
                                  <OperatorIcon job={job} />
                                </span>
                                {isLiveEvidence(job) ? (
                                  <span
                                    className="op-ops-live-ember"
                                    aria-hidden
                                  />
                                ) : null}
                              </span>
                              <span className="op-ops-node-body">
                                <strong>{job.label}</strong>
                                <small>
                                  {job.coverage === "covered"
                                    ? job.operator === "ai_agent"
                                      ? `AI agent${job.agentName ? ` · ${job.agentName}` : ""}${
                                          job.evidence.status === "none"
                                            ? " · Founder-declared"
                                            : ""
                                        }`
                                      : job.operator === "tool"
                                        ? `Tool${job.connectionProvider ? ` · ${job.connectionProvider}` : ""}`
                                        : job.operator === "script"
                                          ? "Script"
                                          : "Manual"
                                    : job.coverage === "uncovered"
                                      ? "Uncovered"
                                      : "Unassessed"}
                                  {isLiveEvidence(job)
                                    ? " · evidence live"
                                    : ""}
                                  {job.evidence.status === "stale"
                                    ? " · stale"
                                    : ""}
                                  {job.evidence.status === "failed"
                                    ? " · failed"
                                    : ""}
                                </small>
                                {score !== null ? (
                                  <span className="op-ops-node-progress">
                                    <AutomationBar
                                      pct={score}
                                      size="sm"
                                      lit={score >= 100}
                                    />
                                    <em>{score}%</em>
                                  </span>
                                ) : null}
                              </span>
                              {job.evidence.status === "stale" ? (
                                <Clock
                                  size={14}
                                  className="op-ops-node-mark"
                                  aria-hidden
                                />
                              ) : null}
                              {job.evidence.status === "failed" ? (
                                <AlertTriangle
                                  size={14}
                                  className="op-ops-node-mark"
                                  aria-hidden
                                />
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : null}
              </section>
            );
          })}
        </div>

        {selected ? (
          <aside
            className={`op-ops-panel${panelLive ? " is-live" : ""}${
              selectedScore !== null && selectedScore >= 100
                ? " is-full-auto"
                : ""
            }`}
            aria-label="Job detail"
          >
            <div className="op-ops-panel-head">
              <h3>{selected.label}</h3>
              <button
                type="button"
                className="op-linkish"
                onClick={() => setSelectedKey(null)}
              >
                Close
              </button>
            </div>
            <p className="op-ops-panel-desc">
              {selected.description ||
                "Recurring work that keeps this product running."}
            </p>

            {selectedScore !== null ? (
              <div className="op-ops-panel-auto">
                <div className="op-ops-panel-auto-top">
                  <strong>{selectedScore}%</strong>
                  <span>{automationLabel(selectedScore)}</span>
                </div>
                <AutomationBar
                  pct={selectedScore}
                  size="md"
                  lit={selectedScore >= 100}
                />
              </div>
            ) : null}

            <label className="op-ops-field">
              Label
              <input
                className="op-field"
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onBlur={() => {
                  if (
                    renameDraft.trim() &&
                    renameDraft.trim() !== selected.label
                  ) {
                    void patchJob(selected.jobKey, {
                      label: renameDraft.trim(),
                    });
                  }
                }}
              />
            </label>

            <fieldset className="op-ops-fieldset">
              <legend>Coverage</legend>
              {(
                [
                  ["unassessed", "Unassessed"],
                  ["uncovered", "Uncovered"],
                  ["covered", "Covered"],
                  ["not_applicable", "Not applicable"],
                ] as const
              ).map(([v, label]) => (
                <label key={v} className="op-ops-radio">
                  <input
                    type="radio"
                    name="coverage"
                    checked={selected.coverage === v}
                    disabled={busy === selected.jobKey}
                    onChange={() =>
                      void patchJob(selected.jobKey, { coverage: v })
                    }
                  />
                  {label}
                </label>
              ))}
            </fieldset>

            {selected.coverage === "covered" ? (
              <fieldset className="op-ops-fieldset">
                <legend>Operator</legend>
                {(
                  [
                    ["me", "Me"],
                    ["ai_agent", "AI agent"],
                    ["script", "Script"],
                    ["tool", "Tool"],
                  ] as const
                ).map(([v, label]) => (
                  <label key={v} className="op-ops-radio">
                    <input
                      type="radio"
                      name="operator"
                      checked={selected.operator === v}
                      disabled={busy === selected.jobKey}
                      onChange={() =>
                        void patchJob(selected.jobKey, { operator: v })
                      }
                    />
                    {label}
                  </label>
                ))}
                <label className="op-ops-check">
                  <input
                    type="checkbox"
                    checked={agentApproval}
                    onChange={(e) => {
                      setAgentApproval(e.target.checked);
                      void patchJob(selected.jobKey, {
                        humanApproval: e.target.checked,
                      });
                    }}
                  />
                  Human approval required
                </label>
              </fieldset>
            ) : null}

            {selected.coverage === "covered" &&
            selected.operator === "ai_agent" ? (
              <div className="op-ops-agent-form">
                <p className="op-ops-hint">
                  AI agent · Founder-declared
                  {selected.evidence.status === "none"
                    ? " · No execution evidence yet"
                    : ""}
                </p>
                <label className="op-ops-field">
                  Agent name
                  <input
                    className="op-field"
                    value={agentNameDraft}
                    onChange={(e) => setAgentNameDraft(e.target.value)}
                    placeholder="e.g. Cursor outbound"
                  />
                </label>
                <button
                  type="button"
                  className="op-primary"
                  disabled={busy === selected.jobKey}
                  onClick={() =>
                    void patchJob(selected.jobKey, {
                      agentName: agentNameDraft.trim() || null,
                      operator: "ai_agent",
                      humanApproval: agentApproval,
                    })
                  }
                >
                  Save agent declaration
                </button>
              </div>
            ) : null}

            {(selected.suggestedProvider ||
              selected.coverage === "covered") && (
              <div className="op-ops-link-block">
                <h4>Connection</h4>
                {selected.suggestedProvider === "instantly" ? (
                  <p className="op-ops-hint">
                    {data?.capabilities?.instantly ||
                      "Instantly can provide outbound sending activity and mailbox warmup health."}{" "}
                    Warmup is supporting — not proof a campaign ran.
                  </p>
                ) : null}
                {selected.suggestedProvider === "buffer" ? (
                  <p className="op-ops-hint">
                    {data?.capabilities?.buffer ||
                      "Buffer can provide social publish and schedule activity."}
                  </p>
                ) : null}
                {selected.connectionProvider ? (
                  <p>
                    Linked · {selected.connectionProvider}
                    {selected.evidence.connectionState === "awaiting_evidence"
                      ? " · Awaiting evidence"
                      : ""}
                    {selected.evidence.status === "recent"
                      ? " · Live evidence"
                      : ""}
                  </p>
                ) : (
                  <p className="op-ops-hint">No connection linked yet.</p>
                )}
                <div className="op-ops-actions">
                  {selected.suggestedProvider === "instantly" &&
                  props.instantlyConnected ? (
                    <button
                      type="button"
                      className="op-primary"
                      disabled={busy === selected.jobKey}
                      onClick={() =>
                        void patchJob(
                          selected.jobKey,
                          {
                            connectionProvider: "instantly",
                            coverage: "covered",
                            operator: "tool",
                          },
                          { flash: true },
                        )
                      }
                    >
                      Link Instantly
                    </button>
                  ) : null}
                  {selected.suggestedProvider === "buffer" &&
                  props.bufferConnected ? (
                    <button
                      type="button"
                      className="op-primary"
                      disabled={busy === selected.jobKey}
                      onClick={() =>
                        void patchJob(
                          selected.jobKey,
                          {
                            connectionProvider: "buffer",
                            coverage: "covered",
                            operator: "tool",
                          },
                          { flash: true },
                        )
                      }
                    >
                      Link Buffer
                    </button>
                  ) : null}
                  {selected.connectionProvider ? (
                    <button
                      type="button"
                      className="op-linkish"
                      onClick={() =>
                        void patchJob(selected.jobKey, {
                          connectionProvider: null,
                        })
                      }
                    >
                      Unlink
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="op-linkish"
                    onClick={props.onManageConnection}
                  >
                    Manage Connection
                  </button>
                </div>
              </div>
            )}

            <div className="op-ops-evidence-block">
              <h4>Evidence</h4>
              {selected.evidence.status === "none" &&
              !selected.evidence.supporting ? (
                <p className="op-ops-hint">No execution evidence yet.</p>
              ) : (
                <>
                  {selected.evidence.summary ? (
                    <p>{selected.evidence.summary}</p>
                  ) : null}
                  {selected.evidence.asOf ? (
                    <small>
                      As of {new Date(selected.evidence.asOf).toLocaleString()}
                      {selected.evidence.evidenceLabel === "agent-reported"
                        ? " · agent-reported"
                        : selected.evidence.source
                          ? ` · ${selected.evidence.source}`
                          : ""}
                    </small>
                  ) : null}
                  {selected.evidence.supporting ? (
                    <p className="op-ops-hint">
                      {(() => {
                        const w = (
                          selected.evidence.supporting as {
                            warmup?: {
                              avgHealthScore?: number | null;
                              inboxRate?: number | null;
                            };
                          }
                        ).warmup;
                        if (!w) {
                          return "Supporting signals available — not campaign proof.";
                        }
                        const health =
                          w.avgHealthScore != null
                            ? `avg health ${w.avgHealthScore}`
                            : null;
                        const inbox =
                          w.inboxRate != null
                            ? `inbox ${Math.round(w.inboxRate * 1000) / 10}%`
                            : null;
                        return `Supporting warmup${health || inbox ? ` · ${[health, inbox].filter(Boolean).join(" · ")}` : ""} — not campaign proof.`;
                      })()}
                    </p>
                  ) : null}
                </>
              )}
            </div>

            <button
              type="button"
              className="op-primary op-ops-quest-cta"
              onClick={() =>
                props.onCreateQuest({
                  jobKey: selected.jobKey,
                  label: selected.label,
                  gap:
                    selected.coverage === "uncovered"
                      ? "uncovered"
                      : selected.evidence.status === "failed"
                        ? "failed_execution"
                        : selected.evidence.status === "stale"
                          ? "stale_evidence"
                          : selected.coverage === "unassessed"
                            ? "unassessed"
                            : "improve",
                })
              }
            >
              <Sparkles size={14} aria-hidden />
              Create improvement quest
            </button>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
